import { Injectable } from '@angular/core';
import { ConfigService } from 'providers/config.service';
import { AuthService } from 'providers/auth.service';
import { HttpClient } from '@angular/common/http';

@Injectable()
export class DevicesApiService {
    constructor(
        private http: HttpClient,
        private authService: AuthService,
        private config: ConfigService
    ) {}

    /**
     * Description
     */
    async internalGetDevice(serialNumber: string): Promise<InternalGetDeviceResponse> {
        return this.http
            .get<InternalGetDeviceResponse>(
                `${this.config.restApiUrl}/internal/devices/${encodeURIComponent(serialNumber)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Create a new Device
     */
    async postDevice(stationId: string, body: PostDeviceRequest): Promise<PostDeviceResponse> {
        return this.http
            .post<PostDeviceResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/devices`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Updates an existing Device
     */
    async putDevice(stationId: string, body: PutDeviceRequest): Promise<PutDeviceResponse> {
        return this.http
            .put<PutDeviceResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/devices`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * Deletes a Device
     */
    async deleteDevice(stationId: string, body: DeleteDeviceRequest): Promise<DeleteDeviceResponse> {
        return this.http
            .delete<DeleteDeviceResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/devices`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                    body,
                } as any
            )
            .toPromise() as any as Promise<DeleteDeviceResponse>;
    }

    /**
     * Get Devices
     */
    async getDevices(stationId: string): Promise<GetDevicesResponse> {
        return this.http
            .get<GetDevicesResponse>(
                `${this.config.restApiUrl}/chargingStations/${encodeURIComponent(stationId)}/devices`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * POST new eichrecht dataset
     */
    async postEichrechtDataSet(body: PostEichrechtDataSetRequest): Promise<PostEichrechtDataSetResponse> {
        return this.http
            .post<PostEichrechtDataSetResponse>(`${this.config.restApiUrl}/eichrecht`, body, {
                headers: {
                    Authorization: `Bearer ${this.authService.jwt}`,
                },
            })
            .toPromise();
    }

    /**
     * GET eichrecht data XML
     */
    async getEichrechtDataSets(meterId: string, contractId: string): Promise<GetEichrechtDataSetsResponse> {
        return this.http
            .get<GetEichrechtDataSetsResponse>(
                `${this.config.restApiUrl}/eichrecht/${encodeURIComponent(meterId)}/${encodeURIComponent(contractId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }

    /**
     * GET eichrecht data sets for specific meter
     */
    async getEichrechtDataSetsForMeter(meterId: string): Promise<GetEichrechtDataSetsForMeterResponse> {
        return this.http
            .get<GetEichrechtDataSetsForMeterResponse>(
                `${this.config.restApiUrl}/eichrecht/${encodeURIComponent(meterId)}`,
                {
                    headers: {
                        Authorization: `Bearer ${this.authService.jwt}`,
                    },
                }
            )
            .toPromise();
    }
}

export interface DeleteDeviceRequest {
    details?: {
        can: string;
    };
    deviceTypeShort: DeviceTypeShort;
    serialNumber?: string;
    satelliteId?: number;
}

export interface DeleteDeviceResponse {
    success: true;
}

export interface GetDeviceResponse {
    /**
     * Contains various details about the device
     */
    details?: OmegaDetails | PowerModuleDetails;
    configurationId?: number;
    /**
     * Station unique identifier.
     */
    stationId?: string;
    /**
     * Device type
     */
    deviceTypeShort?: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
    /**
     * Serial number of the device, e.g. 2400768.
     */
    serialNumber?: string;
    satelliteId?: number;
    /**
     * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
     */
    modelName?: string;
    /**
     * If true, the charger has not reported on this device and it might be actually exist
     */
    stale?: boolean;
}

export interface GetDevicesResponse {
    devices: {
        /**
         * Contains various details about the device
         */
        details?: OmegaDetails | PowerModuleDetails;
        configurationId?: number;
        /**
         * Station unique identifier.
         */
        stationId?: string;
        /**
         * Device type
         */
        deviceTypeShort?: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
        /**
         * Serial number of the device, e.g. 2400768.
         */
        serialNumber?: string;
        satelliteId?: number;
        /**
         * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
         */
        modelName?: string;
        /**
         * If true, the charger has not reported on this device and it might be actually exist
         */
        stale?: boolean;
    }[];
}

export interface GetEichrechtDataSetsResponse {
    xml: string;
}

export interface GetEichrechtDataSetsForMeterResponse {
    dataSets: PickSignedMeterDataset[];
}

export interface Device {
    /**
     * Contains various details about the device
     */
    details?: OmegaDetails | PowerModuleDetails;
    configurationId?: number;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string | null;
    /**
     * Device type
     */
    deviceTypeShort: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
    /**
     * Serial number of the device, e.g. 2400768.
     */
    serialNumber: string;
    satelliteId?: number;
    /**
     * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
     */
    modelName?: string;
    tenantIdEMSP: string;
}

export interface InternalGetDeviceResponse {
    device: {
        /**
         * Contains various details about the device
         */
        details?: OmegaDetails | PowerModuleDetails;
        configurationId?: number;
        /**
         * Station unique identifier.
         */
        stationId: string;
        groupPath?: string | null;
        /**
         * Device type
         */
        deviceTypeShort: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
        /**
         * Serial number of the device, e.g. 2400768.
         */
        serialNumber: string;
        satelliteId?: number;
        /**
         * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
         */
        modelName?: string;
        tenantIdEMSP: string;
    };
}

export interface PostDeviceRequest {
    modelName?: string;
    configurationId?: number;
    details?: OmegaDetails | PowerModuleDetails;
    deviceTypeShort: DeviceTypeShort;
    serialNumber?: string;
    satelliteId?: number;
}

export interface PostDeviceResponse {
    success: true;
}

export interface PostEichrechtDataSetRequest {
    dataSet: SignedMeterDataset;
}

export interface PostEichrechtDataSetResponse {
    success: boolean;
    key: string;
}

export interface PutDeviceRequest {
    /**
     * Contains various details about the device
     */
    details?: OmegaDetails | PowerModuleDetails;
    configurationId?: number;
    groupPath?: string | null;
    /**
     * Device type
     */
    deviceTypeShort?: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
    /**
     * Serial number of the device, e.g. 2400768.
     */
    serialNumber?: string;
    satelliteId?: number;
    /**
     * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
     */
    modelName?: string;
}

export interface PutDeviceResponse {
    /**
     * Row type device
     */
    schema: 'device';
    /**
     * Serial number of the device, e.g. 2400768.
     */
    serialNumber: string;
    /**
     * Device type
     */
    deviceTypeShort: 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc';
    /**
     * Device model name (from manufacturer's ERP), e.g. KEMPOWER C-500 H1
     */
    modelName?: string;
    /**
     * Contains various details about the device
     */
    details?: OmegaDetails | PowerModuleDetails;
    configurationId?: number;
    satelliteId?: number;
    /**
     * Station unique identifier.
     */
    stationId: string;
    groupPath?: string | null;
    _pk: string;
    _sk: string;
    _gsiTenantEMSPPk: string;
    _gsiTenantEMSPSk: string;
    _gsiTenantCPOPk: string;
    _gsiTenantCPOSk: string;
    _gsiTenantMaintenancePk: string;
    _gsiTenantMaintenanceSk: string;
    _gsi1pk: string;
    _gsi1sk: string;
    _gsi2pk: string;
    _gsi2sk: string;
    /**
     * DynamoDB TTL (Time to Live) - item expiry time (unix timestamp).
     * Set for scheduled deletion of item row.
     * Please note this is SECONDS (unix timestamp) not MILLISECONDS (like usually in javascript).
     */
    _ttl?: number;
    tenantIdEMSP: string;
    tenantIdCPO: string;
    tenantIdMaintenance: string;
    schemaVersion?: number;
    versionCreatedTimestamp: string;
    versionCreatedUserId: string;
    versionCreatedUserRole: string;
    versionCreatedTenantId: string;
    versionModifiedTimestamp: string;
    versionModifiedUserId: string;
    versionDeletedTimestamp?: string;
    versionDeletedUserId: string;
    /**
     * Which tenants has access to this row
     */
    tenantIds: string[];
    deleted?: boolean;
}

/**
 * cs = Controller system (Omega) - of Trolleys, or of main cabinet
 * sc = Secondary controller (Omega in Satellites, secondary cabinets)
 * pm = Power module
 * rfid = RFID card reader
 * ledc = LED controller
 * emv = Payment terminal
 */
export type DeviceTypeShort = 'cs' | 'ledc' | 'pm' | 'rfid' | 'sc' | 'emv';

export interface OmegaDetails {
    role?: 'CABINET' | 'SATELLITE' | 'SLAVE_CABINET' | 'STANDALONE' | 'TROLLEY';
    swVersion?: string;
    hwRevision?: string;
}

export interface PowerModuleDetails {
    device?: string;
    family?: string;
    can?: string;
    maxVoltage?: string;
    powerKw?: number;
    manufacturingDate?: string;
    bootVersion?: string;
    applicationVersion?: string;
    featureFlags?: string[];
    boardRevision?: number;
    alerts?: PmcAlert[];
}

export interface PmcAlert {
    /**
     * Example values: 'BROKEN' | 'PARTIALLY_BROKEN'
     */
    severity?: string;
    problem?: string;
}

export interface SignedMeterDatasetDetails {
    /**
     * When the value was recorded, "legal time"
     */
    timestamp: string;
    /**
     * A summary of the meter status at the time, as presented in the `summary` field of the separate meter status message. A more detailed status can be decoded from `signedMeterData`
     */
    meterStatus: 'Error' | 'OK' | 'Warning';
    /**
     * The energy counter value at `timestamp`
     */
    energyCounter: number;
    /**
     * The current phase of the session (present only in `current`)
     */
    phase?: 'End' | 'Intermediate' | 'Start' | 'Unknown';
}

export type SignedMeterSummary = 'Error' | 'OK' | 'Warning';

/**
 * Signed values are collected for a certain phase of the session
 * * `Unknown` The value exists internally, but should never appear in the MQTT API
 */
export type SignedMeterPhase = 'End' | 'Intermediate' | 'Start' | 'Unknown';

/**
 * Whenever a signed session value is requested from the energy meter, it is forwarded to
 * `kempower/<satId>/<conId>/emeter/session/signed_data`
 */
export interface SignedMeterDataset {
    /**
     * When the message was sent. UTC timestamp
     */
    timestamp: string;
    data: {
        /**
         * Meter identification string, hex-encoded. For Isabellenhütte IEM-DCC-500 meters this contains the manufacturer name and serial number
         */
        meterId: string;
        /**
         * As set by the session start request
         */
        contractId: string;
        start?: SignedMeterDatasetDetails;
        current?: SignedMeterDatasetDetails;
        /**
         * The Eichrecht required pagination value from the meter. A per-meter counter value that is incremented by one every time a signed value is retrieved from the meter.
         */
        pagination: number;
        /**
         * The latest logbook index (Eichrecht required calibration-related data log)
         */
        logBookIndex: number;
    };
    ocpp: {
        /**
         * Base64-encoded raw data with embedded signature. Compatible with the S.A.F.E. transparency software
         */
        signedMeterData: string;
        /**
         * A textual description of the signing method used. For the Isabellenhütte IEM-DCC-500, it's ECDSA(secp256r1)
         */
        signingMethod: 'ECDSA(secp256r1)';
        /**
         * A textual description of the encoding method used. For the Isabellenhütte IEM-DCC-500, it is `ISA_EDL_40_P`
         */
        encodingMethod: 'ISA_EDL_40_P';
        /**
         * Base64-encoded raw public key. For the `secp256r1` signing method, this is a concatenation of two 32 byte big endian integers that make up the public key
         */
        publicKey: string;
    };
}

export interface PickSignedMeterDataset {
    data: {
        /**
         * Meter identification string, hex-encoded. For Isabellenhütte IEM-DCC-500 meters this contains the manufacturer name and serial number
         */
        meterId: string;
        /**
         * As set by the session start request
         */
        contractId: string;
        start?: SignedMeterDatasetDetails;
        current?: SignedMeterDatasetDetails;
        /**
         * The Eichrecht required pagination value from the meter. A per-meter counter value that is incremented by one every time a signed value is retrieved from the meter.
         */
        pagination: number;
        /**
         * The latest logbook index (Eichrecht required calibration-related data log)
         */
        logBookIndex: number;
    };
    /**
     * When the message was sent. UTC timestamp
     */
    timestamp: string;
}
